import * as THREE from 'three'
import { gsap } from 'gsap'
import Experience from '../../../Experience'
import { TRIGGER_ZONE, AURORA_SUB_EFFECTS } from '../celestialConfig'
import vertexShader from '../Shader/aurora/vertex.glsl'
import fragmentShader from '../Shader/aurora/fragment.glsl'
import particlesVertexShader from '../Shader/auroraParticles/vertex.glsl'
import particlesFragmentShader from '../Shader/auroraParticles/fragment.glsl'

/**
 * AuroraCurtains — auroraMoon がトリガーゾーンに入った時のエフェクト2層。
 *  1. オーロラカーテン: 星空球(r=27)のすぐ内側に沿った大きな球面バンド
 *     （BackSide、StarrySky と同じ「遠側の内面が見える」方式）に、
 *     空を横切るうねったアーチ＋上方向へ伸びるカーテンのレイを描く。2レイヤー重ね
 *  2. シーン染色: 発動中、オーロラの光が世界を照らすように
 *     星空の uTint・太陽光の色・惑星背後のグローをうっすら緑へ染め、退出で元に戻す
 *  3. 降り注ぐ光の粒: 緑〜紫の粒がキラキラと明滅しながら視界全体を
 *     ゆっくり降下する Points（1ドローコール、ループ降下）
 *
 * エフェクト共通インターフェース: fadeIn / fadeOut / updatePosition / update / dispose
 */
export default class AuroraCurtains
{
  constructor()
  {
    this.experience = new Experience()
    this.scene = this.experience.scene
    this.resources = this.experience.resources
    this.quality = this.experience.quality

    const perlinTexture = this.resources.items.perlinTexture0
    perlinTexture.wrapS = THREE.RepeatWrapping
    perlinTexture.wrapT = THREE.RepeatWrapping

    this.group = new THREE.Group()
    this.materials = []

    // トリガーゾーンの方位（xz平面）にバンドの中心を向ける
    const zoneDir = new THREE.Vector3(...TRIGGER_ZONE.direction).normalize()
    const zoneAzimuth = Math.atan2(zoneDir.x, zoneDir.z)

    // 2レイヤー: 手前は鮮やか、奥は薄く広く
    const layers = [
      { radius: 25.5, phiLength: Math.PI * 1.30, thetaStart: 0.08, thetaLength: 0.42, seed: 0.0, intensityScale: 1.0 },
      { radius: 26.3, phiLength: Math.PI * 1.55, thetaStart: 0.05, thetaLength: 0.50, seed: 0.5, intensityScale: 0.55 },
    ]

    for (const layer of layers) {
      const geometry = new THREE.SphereGeometry(
        layer.radius,
        96, 32,
        0, layer.phiLength,
        Math.PI * layer.thetaStart, Math.PI * layer.thetaLength
      )

      const material = new THREE.ShaderMaterial({
        vertexShader: vertexShader,
        fragmentShader: fragmentShader,
        uniforms: {
          uColorBottom: new THREE.Uniform(new THREE.Color('#4dffb8')),
          uColorTop: new THREE.Uniform(new THREE.Color('#5c7dff')),
          uIntensity: new THREE.Uniform(0),
          uTime: new THREE.Uniform(0),
          uSeed: new THREE.Uniform(layer.seed),
          uPerlinTexture: new THREE.Uniform(perlinTexture),
        },
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.BackSide, // 星空球と同じく、球の遠側の内面として見せる
      })
      material.userData.intensityScale = layer.intensityScale
      this.materials.push(material)

      const mesh = new THREE.Mesh(geometry, material)
      /* SphereGeometry のバンド中心方位（phi = phiLength/2 の方向）が
         トリガーゾーンの方位に来るように Y 回転で合わせる
         （three.js の球の頂点: x = -r·cos(phi)·sinθ, z = r·sin(phi)·sinθ） */
      const phiCenter = layer.phiLength / 2
      const bandAzimuth = Math.atan2(-Math.cos(phiCenter), Math.sin(phiCenter))
      mesh.rotation.y = zoneAzimuth - bandAzimuth
      mesh.renderOrder = 2 // 星空(-1)・ハロー(1)より後に加算描画
      this.group.add(mesh)
    }

    // サブエフェクトの ON/OFF（celestialConfig の AURORA_SUB_EFFECTS が初期値。
    // setSceneTintEnabled / setParticlesEnabled で実行中も切り替え可能）
    this.sceneTintEnabled = AURORA_SUB_EFFECTS.sceneTint
    this.particlesEnabled = AURORA_SUB_EFFECTS.fallingParticles
    this.isActive = false // ゾーン内（fadeIn 済み）かどうか

    this.setFallingParticles()

    this.group.visible = false
    this.scene.add(this.group)

    // シーン染色（0 = 通常, 1 = オーロラ色）。fadeIn/fadeOut で GSAP トゥイーン
    this.tintProgress = { value: 0 }
    this.tintTargets = null // fadeIn 時に遅延解決（生成時点では environment が未生成のため）
  }

  // 降り注ぐ光の粒（Fireflies と同じ Points ＋個体位相のパターン）
  setFallingParticles()
  {
    this.sizes = this.experience.sizes

    const count = 180
    // 視界の空を広く覆う直方体に散らす（カメラ (0,0,30) から見える範囲）
    const area = { x: [-28, 28], y: [-6, 24], z: [-24, 12] }
    this.particleFallHeight = area.y[1] - area.y[0]

    const positions = new Float32Array(count * 3)
    const scales = new Float32Array(count)
    const offsets = new Float32Array(count)
    const colorMixes = new Float32Array(count)

    for (let i = 0; i < count; i++) {
      positions[i * 3 + 0] = area.x[0] + Math.random() * (area.x[1] - area.x[0])
      positions[i * 3 + 1] = area.y[1] // 上端から降下開始（オフセットでばらける）
      positions[i * 3 + 2] = area.z[0] + Math.random() * (area.z[1] - area.z[0])

      scales[i] = 0.5 + Math.random()
      offsets[i] = Math.random()
      colorMixes[i] = Math.pow(Math.random(), 1.5) // 緑多め、たまに紫
    }

    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geometry.setAttribute('aScale', new THREE.BufferAttribute(scales, 1))
    geometry.setAttribute('aOffset', new THREE.BufferAttribute(offsets, 1))
    geometry.setAttribute('aColorMix', new THREE.BufferAttribute(colorMixes, 1))

    this.particlesMaterial = new THREE.ShaderMaterial({
      vertexShader: particlesVertexShader,
      fragmentShader: particlesFragmentShader,
      uniforms: {
        uTime: new THREE.Uniform(0),
        uSize: new THREE.Uniform(380),
        uPixelRatio: new THREE.Uniform(Math.min(this.sizes.pixelRatio, this.quality.maxPixelRatio)),
        uFallHeight: new THREE.Uniform(this.particleFallHeight),
        uFallSpeed: new THREE.Uniform(0.9),
        uIntensity: new THREE.Uniform(0),
        uColorGreen: new THREE.Uniform(new THREE.Color('#7dffc4')),
        uColorPurple: new THREE.Uniform(new THREE.Color('#a98bff')),
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    })

    this.particles = new THREE.Points(geometry, this.particlesMaterial)
    this.particles.renderOrder = 3 // カーテン(2)より後に加算描画
    this.particles.visible = this.particlesEnabled
    this.group.add(this.particles)
  }

  // サブエフェクト: シーン染色の ON/OFF（実行中に OFF にすると元の色へ戻る）
  setSceneTintEnabled(enabled)
  {
    this.sceneTintEnabled = enabled
    this.resolveTintTargets()
    gsap.to(this.tintProgress, {
      value: enabled && this.isActive ? 1 : 0,
      duration: Math.max(2.0 * this.quality.motionScale, 0.01),
      ease: 'power2.inOut',
      overwrite: true,
      onUpdate: () => this.applyTint(),
    })
  }

  // サブエフェクト: 降り注ぐ光の粒の ON/OFF
  setParticlesEnabled(enabled)
  {
    this.particlesEnabled = enabled
    this.particles.visible = enabled
    if (enabled && this.isActive) {
      gsap.to(this.particlesMaterial.uniforms.uIntensity, {
        value: 1.0,
        duration: Math.max(2.0 * this.quality.motionScale, 0.01),
        ease: 'power2.inOut',
        overwrite: true,
      })
    }
  }

  /* 染色対象の遅延解決。初回 fadeIn 時にオリジナル値をキャプチャする */
  resolveTintTargets()
  {
    if (this.tintTargets) return

    const world = this.experience.world
    this.tintTargets = {
      starrySky: world?.StarrySky ?? null,
      sunLight: world?.environment?.sunLight ?? null,
      planetHighlights: world?.PlanetHighlight ?? null,
      // オリジナル値
      originalSkyTint: world?.StarrySky ? world.StarrySky.material.uniforms.uTint.value.clone() : null,
      originalNebula: world?.StarrySky ? world.StarrySky.material.uniforms.uNebulaIntensity.value : 0,
      originalSunColor: world?.environment?.sunLight ? world.environment.sunLight.color.clone() : null,
      originalHighlightTint: world?.PlanetHighlight ? world.PlanetHighlight.material.uniforms.uTint.value.clone() : null,
      // オーロラ色
      auroraSkyTint: new THREE.Color('#a9f0c8'),
      auroraSunColor: new THREE.Color('#bfe8d0'),
      auroraHighlightTint: new THREE.Color(0.6, 1.0, 0.78),
    }
  }

  /* tintProgress に応じて各対象をオリジナル→オーロラ色へ補間 */
  applyTint()
  {
    const t = this.tintTargets
    if (!t) return
    const progress = this.tintProgress.value

    if (t.starrySky) {
      t.starrySky.material.uniforms.uTint.value
        .lerpColors(t.originalSkyTint, t.auroraSkyTint, progress)
      t.starrySky.material.uniforms.uNebulaIntensity.value =
        t.originalNebula * (1.0 + progress) // 発動中は星雲を2倍まで濃く
    }
    if (t.sunLight) {
      t.sunLight.color.lerpColors(t.originalSunColor, t.auroraSunColor, progress)
    }
    if (t.planetHighlights) {
      t.planetHighlights.material.uniforms.uTint.value
        .lerpColors(t.originalHighlightTint, t.auroraHighlightTint, progress)
    }
  }

  fadeIn()
  {
    this.isActive = true
    this.group.visible = true
    for (const material of this.materials) {
      gsap.to(material.uniforms.uIntensity, {
        value: material.userData.intensityScale,
        duration: Math.max(5.0 * this.quality.motionScale, 0.01), // オーロラはゆっくり咲かせる
        ease: 'power2.inOut',
        overwrite: true,
      })
    }

    // 光の粒もカーテンと同期してフェードイン
    if (this.particlesEnabled) {
      gsap.to(this.particlesMaterial.uniforms.uIntensity, {
        value: 1.0,
        duration: Math.max(5.0 * this.quality.motionScale, 0.01),
        ease: 'power2.inOut',
        overwrite: true,
      })
    }

    // カーテンと同期して世界をオーロラ色に染める
    if (this.sceneTintEnabled) {
      this.resolveTintTargets()
      gsap.to(this.tintProgress, {
        value: 1,
        duration: Math.max(5.0 * this.quality.motionScale, 0.01),
        ease: 'power2.inOut',
        overwrite: true,
        onUpdate: () => this.applyTint(),
      })
    }
  }

  fadeOut()
  {
    this.isActive = false
    for (const material of this.materials) {
      gsap.to(material.uniforms.uIntensity, {
        value: 0,
        duration: Math.max(5.0 * this.quality.motionScale, 0.01),
        ease: 'power2.inOut',
        overwrite: true,
        onComplete: () => { this.group.visible = false },
      })
    }

    gsap.to(this.particlesMaterial.uniforms.uIntensity, {
      value: 0,
      duration: Math.max(5.0 * this.quality.motionScale, 0.01),
      ease: 'power2.inOut',
      overwrite: true,
    })

    // 染色を元に戻す（フラグに関わらず必ず復元側へ）
    gsap.to(this.tintProgress, {
      value: 0,
      duration: Math.max(5.0 * this.quality.motionScale, 0.01),
      ease: 'power2.inOut',
      overwrite: true,
      onUpdate: () => this.applyTint(),
    })
  }

  // ゾーン固定なので天体には追従しない
  updatePosition()
  {
  }

  update(elapsed)
  {
    if (!this.group.visible) return

    for (const material of this.materials) {
      material.uniforms.uTime.value = elapsed
    }

    this.particlesMaterial.uniforms.uTime.value = elapsed
    this.particlesMaterial.uniforms.uPixelRatio.value =
      Math.min(this.sizes.pixelRatio, this.quality.maxPixelRatio)
  }

  dispose()
  {
    // 染色を即時復元
    this.tintProgress.value = 0
    this.applyTint()

    this.scene.remove(this.group)
    for (const mesh of this.group.children) {
      mesh.geometry.dispose()
    }
    for (const material of this.materials) {
      material.dispose()
    }
    this.particlesMaterial.dispose()
  }
}
